import { Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IModelItem, INode } from '@yfiles/yfiles';
import { GraphComponentComponent } from '../canvas/graph-component/graph-component.component';
import { INSIGHTS_FIELD, Linqset, MODEL_TYPE } from '../common/linqset-model';
import { GraphService } from '../canvas/graph.service';
import { ModelDetails, Organisation } from '../common/organisation';
import { SCREEN } from '../layouts/layouts-enums';
import { NodeDialogComponent } from '../model-components/nodes/node-dialog.component';
import { FUNCTION, FunctionService } from '../model-services/function.service';
import { UserService } from '../user/user.service';
import { AnalysisScope, modelsInScope, ScopeService } from './scope';
import { ScopeLinqsetService } from './scope-linqset.service';

// The LINQ model itself, with all its actions, information, systems and roles, shown read-only in place of an analysis
// view's summary graph when one model is looked at. The same canvas and toolbar as the Insights model tab; a node's
// details open on a double click
@Component({
    selector: 'app-full-model',
    standalone: false,
    template: `
        @if (error) {
        <p class="error" role="alert">{{ error }}</p>
        } @else if (linqset) {
        <div class="toolbar">
            <app-graph-toolbar [organisation]="organisation" [linqset]="linqset" [graphComponent]="graph?.graphComponent" [readOnly]="true" [screen]="screen"
                (toolbarEvent)="toolbar($event)"></app-graph-toolbar>
        </div>
        <div class="canvas">
            <app-graph-component [linqset]="linqset" [trigger]="trigger" [modelType]="modelType" [readOnly]="true" (itemDoubleClickedEvent)="doubleClicked($event)"></app-graph-component>
        </div>
        }
    `,
    styles: [`
        :host { display: flex; flex-direction: column; position: absolute; inset: 0; }
        .toolbar { flex: none; }
        .canvas { flex: 1 1 auto; position: relative; min-height: 0; }
        .error { margin: 16px; color: #9b1c1c; }
    `],
})
export class FullModelComponent implements OnChanges, OnDestroy {
    @Input() organisation?: Organisation;
    @Input() scope?: AnalysisScope;
    // the canvas is handed to the toolbar a tick after it appears, so the toolbar's binding changes between checks, not during one
    @ViewChild(GraphComponentComponent) set graphView(g: GraphComponentComponent | undefined) {
        if (g !== this.graph) window.setTimeout(() => this.graph = g);
    }
    graph?: GraphComponentComponent;

    readonly screen = SCREEN.INSIGHTS;
    linqset?: Linqset;
    modelType: MODEL_TYPE = MODEL_TYPE.PROCESS;
    trigger: any = {};
    error = '';
    private request = 0;

    constructor(private scopeLinqset: ScopeLinqsetService, private functionService: FunctionService, private userService: UserService, private dialog: MatDialog,
        private graphService: GraphService, private scopeService: ScopeService, private snackbar: MatSnackBar) {}

    // colours the model by an attribute (the one the toolbar's labels show, so the two agree), or clears the colouring
    async showHeatmap(field?: INSIGHTS_FIELD) {
        const graph = this.graph, linqset = this.linqset;
        if (!graph || !linqset) return;
        if (!field || field === INSIGHTS_FIELD.NONE) {
            this.graphService.removeHeatmap(graph.graphComponent);
            return;
        }
        await this.functionService.executeFunction(FUNCTION.MANAGE_LABELS, field, linqset, linqset.project, graph.graphComponent, graph);
        this.graphService.addHeatmap(graph.graphComponent, linqset.maxEdgeValue, linqset.maxNodeValue, linqset.isOrganisationModel());
    }

    // highlights everything connected to the node and zooms to it; false when the node isn't drawn
    trace(key?: string): boolean {
        const gc = this.graph?.graphComponent;
        if (!gc) return false;
        gc.selection.clear();
        if (!key) return true;
        const node = gc.graph.nodes.find(n => n.tag?.node?._key === key);
        if (!node) return false;
        gc.selection.add(node);
        this.graphService.selectConnected(gc, [node]);
        void gc.zoomToAnimated(1, node.layout.center);
        return true;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['organisation'] || changes['scope']) void this.load();
    }

    ngOnDestroy() {
        this.request++;
    }

    private async load() {
        const organisation = this.organisation, scope = this.scope;
        if (!organisation || !scope || modelsInScope(scope, organisation).length !== 1) return;
        const request = ++this.request;
        this.error = '';
        try {
            const { linqset } = await this.scopeLinqset.load(organisation, scope, this.userService.getUser()?._key);
            if (request !== this.request) return;
            this.modelType = linqset.defaultModelType;
            this.linqset = linqset;
            this.trigger = {};
        } catch (err) {
            if (request !== this.request) return;
            console.error('Unable to load the model', err);
            this.error = "The model couldn't be loaded. Please try again.";
        }
    }

    doubleClicked(item: IModelItem) {
        const graph = this.graph;
        if (!(item instanceof INode) || !graph || !this.organisation || !this.linqset) return;
        if (graph.graphComponent.graph.isGroupNode(item) || item.tag?.group) graph.toggleGroupNode(item);
        else if (item.tag?.model) this.openModel(item.tag.model);
        else if (item.tag?.node) NodeDialogComponent.open(this.dialog, this.organisation, graph.graphComponent, this.linqset, this.scopeLinqset.organisationLinqset!, item);
    }

    // a model joined to this one, shown in its place: the views look at it instead, so it is the one drawn here
    private openModel(model: ModelDetails) {
        const details = this.organisation?.models?.find(m => m._key === model._key);
        if (!details || details.role === undefined || details.role === null || details.role < 0) {
            this.snackbar.open(`You don't have access to ${model.name}`, 'close', { duration: 4000 });
            return;
        }
        if (this.scope?.modelKeys.length === 1 && this.scope.modelKeys[0] === model._key) return;
        this.scopeService.narrowTo([model._key]);
    }

    async toolbar(event: { function: FUNCTION; value?: any }) {
        if (!this.linqset || !this.graph) return;
        // a different view of the model is drawn by the canvas when its model type changes, as on the insights screen
        if (event.function === FUNCTION.CHANGE_MODEL_TYPE) {
            this.modelType = event.value;
            return;
        }
        try {
            await this.functionService.executeFunction(event.function, event.value, this.linqset, this.linqset.project, this.graph.graphComponent, this.graph);
        } catch (err) {
            console.error(err);
        }
    }
}
